// Inspiration from https://www.npmjs.com/package/react-responsive-masonry
import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useWindowSize } from 'usehooks-ts';

const DESKTOP_BREAKPOINT = 768;

export type BreakpointMapType = {
  [key: string]: number;
};

const BREAKPOINT_MAP: BreakpointMapType = {
  '320': 1,
  '1024': 2,
};

export interface MasonryGridProps {
  children: React.ReactNode;
  mobileColumnGap?: number;
  mobileRowGap?: number;
  desktopColumnGap?: number;
  desktopRowGap?: number;
  breakpointMap?: BreakpointMapType;
  className?: string;
}

/**
 * Masonry Grid
 *
 * Renders a masonry style grid with a variable number of columns based on the current viewport width.
 * The total columns and column breakpoints can be configured via the `breakpointMap` prop.
 * The specific column and row gaps are customizable via the `columnGap` and `rowGap` props.
 *
 * @param children - Child elements to render within the grid
 * @param mobileColumnGap - The gap between columns in mobile
 * @param mobileRowGap - The gap between rows in mobile
 * @param desktopColumnGap - The gap between columns in desktop
 * @param desktopRowGap - The gap between rows in desktop
 * @param breakpointMap - The map of breakpoints to column counts
 * @param className - The class name to apply to the grid wrapper
 *
 * @returns A masonry style grid organized left to right based on the given children.
 *
 * @example
 * Here's an example of how to use the MasonryGrid component.
 * ```tsx
 * import { MasonryGrid, BreakpointMapType } from '~/components/ui/MasonryGrid';
 *
 * const customBreakpointMap: BreakpointMapType = {
 *  '320': 1,
 *  '768': 2,
 *  '1024': 3,
 * };
 *
 * <MasonryGrid
 *   mobileColumnGap={16}
 *   mobileRowGap={16}
 *   desktopColumnGap={32}
 *   desktopRowGap={32}
 *   breakpointMap={customBreakpointMap}
 *   className="my-custom-class"
 * >
 *   <div>Card 1</div>
 *   <div>Card 2</div>
 *   <div>Card 3</div>
 * </MasonryGrid>
 * ```
 */
export function MasonryGrid({
  children,
  mobileColumnGap = 16,
  mobileRowGap = 16,
  desktopColumnGap = 16,
  desktopRowGap = 16,
  breakpointMap = BREAKPOINT_MAP,
  className,
}: MasonryGridProps): JSX.Element {
  const { width } = useWindowSize();
  const breakPoints = Object.keys(breakpointMap);

  /**
   * Using the current viewport width and the given breakpointMap,
   *  determine the number of columns to render.
   */
  const colCount: number = useMemo(() => {
    let count = 1;
    breakPoints.forEach((breakPoint) => {
      if (Number(breakPoint) < width) {
        count = breakpointMap[breakPoint] as number;
      }
    });

    return count;
  }, [width, breakPoints, breakpointMap]);

  /**
   * Given the number of columns, organize the children into columns. Iterates over the children elements
   * and places them into columns based on the current childs index mod column count. This places each element
   * into the correct column as if you were reading left to right.
   *
   * @param colCount - The number of columns to render
   *
   * @returns An array of columns, each containing an array of child elements
   */
  const getColumns = useCallback(
    (colCount: number) => {
      const cols: Array<Array<React.ReactElement>> = Array.from(
        { length: colCount },
        () => [],
      );

      React.Children.forEach(children, (child, i) => {
        if (child && React.isValidElement(child)) {
          cols[i % colCount].push(child);
        }
      });

      return cols;
    },
    [children],
  );

  /**
   * Render the columns into the grid. Applies the row gap to each column.
   * @param cols - The columns to render
   * @returns Columns rendered into the grid as React elements
   */
  function renderColumns(cols: Array<Array<React.ReactElement>>) {
    return cols.map((col, i) => (
      <div
        key={i}
        className="flex flex-1 flex-col justify-start overflow-hidden"
        style={{
          rowGap:
            width < DESKTOP_BREAKPOINT
              ? `${mobileRowGap}px`
              : `${desktopRowGap}px`,
        }}
        data-testid={`masonry-grid-column-${i}`}
      >
        {col.map((item) => item)}
      </div>
    ));
  }

  const columnsToRender = useMemo(
    () => getColumns(colCount),
    [colCount, getColumns],
  );

  return (
    <div
      className={clsx('flex flex-row', className)}
      style={{
        columnGap:
          width < DESKTOP_BREAKPOINT
            ? `${mobileColumnGap}px`
            : `${desktopColumnGap}px`,
      }}
      data-testid="masonry-grid-wrapper"
    >
      {renderColumns(columnsToRender)}
    </div>
  );
}
